import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  GridActionsCellItem,
  GridColDef,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { GetAllProductsQuery, PackageType } from '../../types';
import { capitalizeFirstLetterAndSpaceSnakeString } from '../../utils/bills-utils';

interface UsePackageColumnsProps {
  onEdit: (row: PackageType) => void;
  onView: (row: PackageType) => void;
  onDelete: (row: PackageType) => void;
  products?: GetAllProductsQuery['getAllProducts'];
  canWrite: boolean;
}

export const usePackageColumns = ({
  onEdit,
  onView,
  onDelete,
  products,
  canWrite,
}: UsePackageColumnsProps): GridColDef[] =>
  useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Package Name',
        flex: 0.8,
        minWidth: 150,
        valueFormatter: (params): string =>
          capitalizeFirstLetterAndSpaceSnakeString(params.value),
      },
      {
        field: 'offeringType',
        headerName: 'Offering Type',
        flex: 0.6,
        minWidth: 150,
        valueGetter: (params): string => {
          const hasProduct = params.row.packageOfferings?.some(
            (o: { packageOffering: string }) => o.packageOffering === 'product',
          );
          return hasProduct ? 'Product' : 'Program';
        },
      },
      {
        field: 'details',
        headerName: 'Details',
        flex: 1.6,
        minWidth: 250,
        valueGetter: (params): string => {
          const productOffering = params.row.packageOfferings?.find(
            (o: { packageOffering: string }) => o.packageOffering === 'product',
          );
          if (productOffering) {
            const product = products?.find(
              p => p.id === productOffering.productId,
            );
            return product
              ? capitalizeFirstLetterAndSpaceSnakeString(product.name)
              : `Product ID: ${productOffering.productId}`;
          }
          const indications = params.row.packageOfferings
            ?.filter(
              (o: { packageOffering: string; programIndication?: string }) =>
                o.packageOffering === 'program_indication' &&
                o.programIndication,
            )
            .map((o: { programIndication: string }) =>
              capitalizeFirstLetterAndSpaceSnakeString(o.programIndication),
            )
            .join(', ');
          return indications || '';
        },
        renderCell: (params): string => {
          const value = params.value as string;
          const maxLength = 60;
          return value.length > maxLength
            ? `${value.slice(0, maxLength)}...`
            : value;
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        type: 'actions',
        width: 130,
        getActions: (params: GridRowParams<PackageType>): JSX.Element[] => {
          const editable = params.row.isEditable === true;
          const actions = [
            <GridActionsCellItem
              key={`view-${params.id}`}
              icon={<VisibilityIcon />}
              label="View"
              onClick={(): void => onView(params.row)}
            />,
          ];
          if (canWrite && editable) {
            actions.push(
              <GridActionsCellItem
                key={`edit-${params.id}`}
                icon={<EditIcon />}
                label="Edit"
                onClick={(): void => onEdit(params.row)}
              />,
              <GridActionsCellItem
                key={`delete-${params.id}`}
                icon={<DeleteOutlineIcon />}
                label="Delete"
                onClick={(): void => onDelete(params.row)}
              />,
            );
          }
          return actions;
        },
      },
    ],
    [onEdit, onView, onDelete, products, canWrite],
  );
